import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, catchError } from 'rxjs';
import { BaseModal } from 'shared/abstract/base-modal';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { EventBusService } from 'shared/services/event-bus.service';
import { ToastService } from 'shared/services/toast.service';
import { UserApiService } from 'shared/services/userApi.service';
import { keyvalueOriginalOrder } from 'shared/util/key-value-original-order';
import { emailListPattern } from 'shared/validators/email-list-pattern-validator';
import { CreateSupplierUser } from 'users/interfaces/create-user.interface';
import { InviteAccessGroup, InviteUserGroup, SuppliersInviteGroup } from 'users/interfaces/suppliers-invite-group.interface';

@Component({
    selector: 'app-create-user',
    templateUrl: './supplier-user-invite.component.html',
    styleUrls: ['./supplier-user-invite.component.scss'],
    standalone: false,
})
export class SupplierUserInviteComponent extends BaseModal implements OnInit {
    public data: any;

    protected userGroups: InviteUserGroup[] | undefined = [];
    protected accessGroups: InviteAccessGroup[] = [];
    protected suppliers!: SuppliersInviteGroup[];
    protected selectedSupplier!: SuppliersInviteGroup | null;
    protected isShijiUser!: boolean;
    protected sorter = keyvalueOriginalOrder;
    protected newUserForm: UntypedFormGroup = this.formBuilder.group({
        supplier: new UntypedFormControl(null, [Validators.required]),
        emails: new UntypedFormControl(null),
        userGroup: new UntypedFormControl({ value: null, disabled: true }, [Validators.required]),
        accessGroup: new UntypedFormControl({ value: [] }),
        contact: new UntypedFormControl(null),
        doNotEmail: new UntypedFormControl(null),
    });

    constructor(
        private userApiService: UserApiService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private eventBusService: EventBusService,
        private formBuilder: FormBuilder,
        private destroyRef: DestroyRef,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.userApiService
            .getLoginInformation()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((loginInfo) => {
                this.isShijiUser = loginInfo.isShiji;

                if (this.isShijiUser) {
                    this.userApiService
                        .getAllSuppliersGroupsAndContact()
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe((suppliers) => (this.suppliers = suppliers));

                    this.newUserForm
                        .get('supplier')
                        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe((value) => {
                            this.selectedSupplier = this.suppliers.find((supplier) => supplier.supplierId === value) ?? null;

                            this.newUserForm.get('supplier')!.setValue(this.selectedSupplier?.supplierId, { emitEvent: false });
                            this.userGroups = this.selectedSupplier?.userGroups;
                            this.preselectUserGroup(this.userGroups);

                            this.accessGroups = this.selectedSupplier?.accessGroups ?? [];
                            this.newUserForm.get('accessGroup')!.reset();
                        });
                } else {
                    this.userApiService
                        .getSingleSupplierGroupsAndContact((this.userApiService.currentUser as CurrentSupplierUserInterface)?.supplierId!)
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe((supplier: SuppliersInviteGroup) => {
                            this.selectedSupplier = supplier;
                            this.newUserForm.get('supplier')?.setValue(supplier.supplierId);
                            this.userGroups = supplier.userGroups;
                            this.accessGroups = supplier.accessGroups ?? [];
                            this.preselectUserGroup(this.userGroups);
                        });
                }
            });

        this.updateEmailValidators(false);
        this.newUserForm
            .get('contact')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => this.updateEmailValidators(value));

        this.eventBusService
            .getObservableForEventStream('closeUserModal')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.closeModalInstance(undefined));
    }

    private preselectUserGroup(groups: InviteUserGroup[] | undefined) {
        if (!groups || !groups.length) {
            this.newUserForm.get('userGroup')?.setValue(undefined);
        } else if (groups.length >= 1) {
            this.newUserForm.get('userGroup')?.setValue(groups[0].userGroupId);
        }
        this.newUserForm.get('userGroup')?.enable();
        this.newUserForm.get('userGroup')?.updateValueAndValidity();
    }

    public createUser(): void {
        this.newUserForm.markAllAsTouched();
        this.newUserForm.get('emails')?.updateValueAndValidity();
        if (this.newUserForm.valid) {
            const emails = this.newUserForm.value.emails.split(',').filter((email: any) => email !== '');
            const supplierName =
                this.suppliers?.find((supplier) => supplier.supplierId === this.newUserForm.value.supplier)?.companyName ??
                this.selectedSupplier?.companyName;

            const body = {
                supplierId: this.newUserForm.value.supplier,
                supplierName: supplierName,
                emails: emails,
                isContact: this.newUserForm.value.contact ?? false,
                emailsEnabled: !(this.newUserForm.value.doNotEmail ?? false),
                groupIds: this.newUserForm.value.userGroup ? [this.newUserForm.value.userGroup] : [],
                listingAccessGroupIds: this.newUserForm.value.accessGroup?.length ? this.newUserForm.value.accessGroup : [],
            } as CreateSupplierUser;

            this.userApiService
                .createSupplierUserInvitation(body)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    catchError((error) => {
                        if (error.status === 409) {
                            this.toastService.showToast('users.supplierUsers.create.userAlreadyExistsOrInvitationExpired', 'error');
                        }

                        return EMPTY;
                    }),
                )
                .subscribe((response: any) => {
                    if (response.invalidEmails?.length) {
                        this.toastService.showToast(
                            this.translateService.instant('users.supplierUsers.create.alreadyCreated') +
                                response.invalidEmails.join('\n').toString(),
                            'error',
                        );
                    } else {
                        this.toastService.showToast(
                            `users.supplierUsers.create.invite${emails.length > 1 ? 'Multiple' : ''}SupplierUserSuccess`,
                            'success',
                            { user: emails, supplier: supplierName },
                        );
                        this.closeModal();
                    }
                });
        }
    }

    closeModal() {
        this.eventBusService.dispatch('closeUserModal');
    }

    private updateEmailValidators(contact: boolean) {
        if (contact) {
            this.newUserForm.get('emails')?.setValidators([Validators.required, Validators.email]);
        } else {
            this.newUserForm.get('emails')?.setValidators([Validators.required, emailListPattern()]);
        }
        this.newUserForm.get('emails')?.updateValueAndValidity();
    }
}
